import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Label } from '../ui/label';
import {
  Bug, Loader2, Trash2, Monitor, Globe, Clock, Download, Upload,
  AlertTriangle, CheckCircle, Circle, ChevronDown, ChevronUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../utils/api';

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open', icon: Circle, color: 'text-blue-500' },
  { value: 'in_progress', label: 'In Progress', icon: AlertTriangle, color: 'text-amber-500' },
  { value: 'resolved', label: 'Resolved', icon: CheckCircle, color: 'text-green-500' },
  { value: 'closed', label: 'Closed', icon: CheckCircle, color: 'text-muted-foreground' },
];

function StatusBadge({ status }) {
  const opt = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
  const Icon = opt.icon;
  const variants = { open: 'default', in_progress: 'secondary', resolved: 'outline', closed: 'outline' };
  return (
    <Badge variant={variants[status] || 'default'} className="gap-1" data-testid={`status-badge-${status}`}>
      <Icon className={`w-3 h-3 ${opt.color}`} /> {opt.label}
    </Badge>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '\u2014';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ============== REPORT DETAIL VIEW (shared by DB reports and uploaded files) ==============

function ReportDetailView({ report }) {
  return (
    <div className="space-y-4 py-2">
      <div>
        <Label className="text-xs text-muted-foreground">Description</Label>
        <p className="text-sm mt-1 whitespace-pre-wrap">{report.description}</p>
      </div>

      {report.screenshot && (
        <div>
          <Label className="text-xs text-muted-foreground">Screenshot</Label>
          <img
            src={report.screenshot}
            alt="Bug screenshot"
            className="mt-1 rounded border border-border max-h-64 w-full object-contain bg-muted"
            data-testid="report-screenshot"
          />
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-muted-foreground flex items-center gap-1"><Globe className="w-3 h-3" /> Page</Label>
          <p className="text-xs mt-0.5 font-mono break-all">{report.page_url}</p>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground flex items-center gap-1"><Monitor className="w-3 h-3" /> Screen</Label>
          <p className="text-xs mt-0.5 font-mono">{report.screen_size}</p>
        </div>
      </div>

      <div>
        <Label className="text-xs text-muted-foreground">Browser</Label>
        <p className="text-xs mt-0.5 font-mono break-all">{report.user_agent}</p>
      </div>

      {report.console_errors?.length > 0 && (
        <div>
          <Label className="text-xs text-muted-foreground flex items-center gap-1">
            <AlertTriangle className="w-3 h-3 text-amber-500" /> Console Errors ({report.console_errors.length})
          </Label>
          <div className="mt-1 p-2 bg-muted rounded text-xs font-mono max-h-40 overflow-y-auto space-y-0.5">
            {report.console_errors.map((err, i) => (
              <p key={i} className="text-destructive/80 break-all">{err}</p>
            ))}
          </div>
        </div>
      )}

      {report.admin_notes && (
        <div>
          <Label className="text-xs text-muted-foreground">Admin Notes</Label>
          <p className="text-sm mt-1 whitespace-pre-wrap text-muted-foreground">{report.admin_notes}</p>
        </div>
      )}
    </div>
  );
}


export function BugReportsPanel() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open');
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Detail dialog (DB report - editable)
  const [selectedReport, setSelectedReport] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reportDetail, setReportDetail] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [statusUpdate, setStatusUpdate] = useState('');
  const [saving, setSaving] = useState(false);

  // Upload viewer
  const [uploadedReports, setUploadedReports] = useState(null);
  const [showUploadViewer, setShowUploadViewer] = useState(false);
  const [uploadViewerIndex, setUploadViewerIndex] = useState(0);
  const fileInputRef = useRef(null);

  const [expandedErrors, setExpandedErrors] = useState({});

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const { ok, data } = await api.get(`/admin/reports?status=${filter}`);
      if (ok) setReports(data);
      else toast.error('Failed to fetch reports');
    } catch { toast.error('Failed to fetch reports'); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchReports(); }, [fetchReports]);
  useEffect(() => { setSelectedIds(new Set()); }, [filter]);

  // ============== SELECTION ==============

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === reports.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(reports.map(r => r.report_id)));
    }
  };

  // ============== DOWNLOAD ==============

  const downloadJson = (data, filename) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadSingle = async (reportId) => {
    try {
      const { ok, data } = await api.get(`/admin/reports/${reportId}`);
      if (ok) {
        const exportData = {
          version: 1,
          exported_at: new Date().toISOString(),
          count: 1,
          reports: [data],
        };
        const dateStr = new Date().toISOString().slice(0, 10);
        downloadJson(exportData, `bug_report_${dateStr}_${reportId}.json`);
        toast.success('Report downloaded');
      } else toast.error('Failed to download');
    } catch { toast.error('Failed to download'); }
  };

  const handleDownloadSelected = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    try {
      const { ok, data } = await api.post('/admin/reports/bulk-download', { report_ids: ids });
      if (ok) {
        const dateStr = new Date().toISOString().slice(0, 10);
        downloadJson(data, `bug_reports_${dateStr}_${ids.length}.json`);
        toast.success(`Downloaded ${ids.length} report${ids.length !== 1 ? 's' : ''}`);
      } else toast.error('Failed to download');
    } catch { toast.error('Failed to download'); }
  };

  // ============== UPLOAD / VIEW ==============

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const parsed = JSON.parse(evt.target.result);
        let reportsArr = [];
        if (parsed.reports && Array.isArray(parsed.reports)) {
          reportsArr = parsed.reports;
        } else if (parsed.report_id) {
          reportsArr = [parsed];
        } else {
          toast.error('Unrecognized report format');
          return;
        }
        setUploadedReports(reportsArr);
        setUploadViewerIndex(0);
        setShowUploadViewer(true);
      } catch {
        toast.error('Invalid JSON file');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // ============== DETAIL (DB report) ==============

  const openDetail = async (report) => {
    setSelectedReport(report);
    setDetailLoading(true);
    try {
      const { ok, data } = await api.get(`/admin/reports/${report.report_id}`);
      if (ok) {
        setReportDetail(data);
        setAdminNotes(data.admin_notes || '');
        setStatusUpdate(data.status);
      }
    } catch { toast.error('Failed to load report'); }
    finally { setDetailLoading(false); }
  };

  const handleUpdate = async () => {
    if (!reportDetail) return;
    setSaving(true);
    try {
      const payload = {};
      if (statusUpdate !== reportDetail.status) payload.status = statusUpdate;
      if (adminNotes !== (reportDetail.admin_notes || '')) payload.admin_notes = adminNotes;
      if (Object.keys(payload).length === 0) { toast('No changes to save'); setSaving(false); return; }
      const { ok } = await api.patch(`/admin/reports/${reportDetail.report_id}`, payload);
      if (ok) { toast.success('Report updated'); setSelectedReport(null); fetchReports(); }
      else toast.error('Failed to update');
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (reportId) => {
    try {
      const { ok } = await api.delete(`/admin/reports/${reportId}`);
      if (ok) { toast.success('Report deleted'); setSelectedReport(null); setSelectedIds(prev => { const n = new Set(prev); n.delete(reportId); return n; }); fetchReports(); }
      else toast.error('Failed to delete');
    } catch { toast.error('Failed to delete'); }
  };

  const toggleErrors = (id, e) => {
    e.stopPropagation();
    setExpandedErrors(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // ============== RENDER ==============

  return (
    <div className="space-y-4" data-testid="bug-reports-panel">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="report-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Reports</SelectItem>
              {STATUS_OPTIONS.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground">{reports.length} report{reports.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="flex items-center gap-2">
          {selectedIds.size > 0 && (
            <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={handleDownloadSelected} data-testid="download-selected-btn">
              <Download className="w-3.5 h-3.5" /> Download {selectedIds.size} Selected
            </Button>
          )}
          <input ref={fileInputRef} type="file" accept=".json" className="hidden" onChange={handleFileUpload} />
          <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={() => fileInputRef.current?.click()} data-testid="upload-report-btn">
            <Upload className="w-3.5 h-3.5" /> View Exported
          </Button>
        </div>
      </div>

      {/* Select all bar */}
      {reports.length > 0 && (
        <div className="flex items-center gap-2 px-1">
          <Checkbox
            checked={reports.length > 0 && selectedIds.size === reports.length}
            onCheckedChange={toggleSelectAll}
            data-testid="select-all-reports"
          />
          <span className="text-xs text-muted-foreground">
            {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Select all'}
          </span>
        </div>
      )}

      {/* Report List */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : reports.length === 0 ? (
        <p className="text-center text-muted-foreground py-8">No {filter !== 'all' ? filter : ''} reports</p>
      ) : (
        <div className="space-y-2">
          {reports.map((r) => (
            <Card
              key={r.report_id}
              className={`hover:border-primary/30 transition-colors cursor-pointer ${selectedIds.has(r.report_id) ? 'border-primary/50 bg-primary/5' : ''}`}
              onClick={() => openDetail(r)}
              data-testid={`report-card-${r.report_id}`}
            >
              <CardContent className="py-3 px-4">
                <div className="flex items-start gap-3">
                  <div className="pt-0.5" onClick={(e) => toggleSelect(r.report_id, e)}>
                    <Checkbox
                      checked={selectedIds.has(r.report_id)}
                      data-testid={`select-report-${r.report_id}`}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{r.description}</p>
                    <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                      <span className="text-xs text-muted-foreground">{r.user_name} ({r.user_email})</span>
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {formatDate(r.created_at)}
                      </span>
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Globe className="w-3 h-3" /> {r.page_url?.replace(/^https?:\/\/[^/]+/, '') || '/'}
                      </span>
                    </div>
                    {r.console_errors?.length > 0 && (
                      <button
                        className="mt-1.5 text-xs text-amber-500 flex items-center gap-1"
                        onClick={(e) => toggleErrors(r.report_id, e)}
                      >
                        <AlertTriangle className="w-3 h-3" />
                        {r.console_errors.length} console error{r.console_errors.length !== 1 ? 's' : ''}
                        {expandedErrors[r.report_id] ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      </button>
                    )}
                    {expandedErrors[r.report_id] && r.console_errors?.length > 0 && (
                      <div className="mt-2 p-2 bg-muted rounded text-xs font-mono max-h-32 overflow-y-auto space-y-0.5" onClick={(e) => e.stopPropagation()}>
                        {r.console_errors.map((err, i) => (
                          <p key={i} className="text-destructive/80 break-all">{err}</p>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="shrink-0">
                    <StatusBadge status={r.status} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* DB Report Detail Dialog (editable) */}
      <Dialog open={!!selectedReport} onOpenChange={() => setSelectedReport(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : reportDetail ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Bug className="w-5 h-5 text-primary" /> Bug Report
                </DialogTitle>
                <DialogDescription>
                  Submitted by {reportDetail.user_name} ({reportDetail.user_email}) &bull; {formatDate(reportDetail.created_at)}
                </DialogDescription>
              </DialogHeader>

              <ReportDetailView report={reportDetail} />

              <div className="border-t border-border pt-3 space-y-3">
                <div className="flex items-center gap-3">
                  <Label className="text-xs shrink-0">Status</Label>
                  <Select value={statusUpdate} onValueChange={setStatusUpdate}>
                    <SelectTrigger className="h-8 w-[160px] text-xs" data-testid="report-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Admin Notes</Label>
                  <textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Internal notes about this report..."
                    className="w-full min-h-[60px] px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-ring resize-y"
                    data-testid="report-admin-notes"
                  />
                </div>
              </div>

              <DialogFooter className="gap-2">
                <Button
                  variant="outline" size="sm"
                  className="text-destructive border-destructive/30 hover:bg-destructive/10 mr-auto"
                  onClick={() => handleDelete(reportDetail.report_id)}
                  data-testid="delete-report-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleDownloadSingle(reportDetail.report_id)} data-testid="download-single-btn">
                  <Download className="w-4 h-4 mr-1" /> Download
                </Button>
                <Button variant="ghost" onClick={() => setSelectedReport(null)}>Cancel</Button>
                <Button onClick={handleUpdate} disabled={saving} data-testid="save-report-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save Changes
                </Button>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Upload Viewer Dialog (read-only) */}
      <Dialog open={showUploadViewer} onOpenChange={setShowUploadViewer}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {uploadedReports && uploadedReports.length > 0 ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5 text-primary" /> Uploaded Report
                  {uploadedReports.length > 1 && (
                    <span className="text-sm font-normal text-muted-foreground">
                      ({uploadViewerIndex + 1} of {uploadedReports.length})
                    </span>
                  )}
                </DialogTitle>
                <DialogDescription>
                  {uploadedReports[uploadViewerIndex]?.user_name && (
                    <>Submitted by {uploadedReports[uploadViewerIndex].user_name} ({uploadedReports[uploadViewerIndex].user_email}) &bull; </>
                  )}
                  {formatDate(uploadedReports[uploadViewerIndex]?.created_at)}
                  {uploadedReports[uploadViewerIndex]?.status && (
                    <> &bull; <StatusBadge status={uploadedReports[uploadViewerIndex].status} /></>
                  )}
                </DialogDescription>
              </DialogHeader>

              <ReportDetailView report={uploadedReports[uploadViewerIndex]} />

              <DialogFooter className="gap-2">
                {uploadedReports.length > 1 && (
                  <div className="flex items-center gap-2 mr-auto">
                    <Button
                      variant="outline" size="sm"
                      disabled={uploadViewerIndex === 0}
                      onClick={() => setUploadViewerIndex(i => i - 1)}
                      data-testid="prev-report-btn"
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline" size="sm"
                      disabled={uploadViewerIndex === uploadedReports.length - 1}
                      onClick={() => setUploadViewerIndex(i => i + 1)}
                      data-testid="next-report-btn"
                    >
                      Next
                    </Button>
                  </div>
                )}
                <Button variant="ghost" onClick={() => setShowUploadViewer(false)}>Close</Button>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
